import type { Form, OptionProduct } from '@linktivity/ars-neo';
import type { SelectedOption } from '@link/share/business/OptionCard';
import type { UnitQuantity } from '@link/share/business/SelectOption';
import {
  computeOptionTimeWindow,
  isOpenDatePlan,
  isSameDayLinkage,
  isTimeOutsideWindow,
  sumQuantities,
  unitQuantitiesToPlanUnitItems
} from '@link/share/utils';

/*
 * Picked add-ons, as the booking keeps them. The shapes are share's
 * `SelectedOption` and the session's `Form`, which the payment panel and the
 * session builder read. From share's `OptionCard`, unchanged in behaviour
 * except where noted.
 */

export interface OptionPick {
  planId: string;
  /** YYYY-MM-DD */
  targetDate: string;
  startTimeId: string;
  /** The start time's `time`. */
  startTime: string;
  /** Every unit type the plan sells that day, zeros included. */
  unitQuantities: UnitQuantity[];
}

/** An option picked in the editor, as the booking keeps it. */
export function toSelectedOption(
  option: OptionProduct,
  pick: OptionPick
): SelectedOption {
  const activity = option.productContent?.activity;
  const plan =
    activity?.plans?.find(item => item.id === pick.planId) ??
    activity?.plans?.[0];
  const image = activity?.imageUrls?.[0] ?? '';
  return {
    productContentId: option.productContent?.id ?? '',
    targetDate: pick.targetDate,
    isOpenDate: isOpenDatePlan(plan),
    unitQuantities: pick.unitQuantities,
    startTimeId: pick.startTimeId,
    startTime: pick.startTime,
    activityForm: {
      activityId: activity?.id ?? '',
      activityTitle: activity?.title ?? '',
      displayCurrency: option.displayCurrency,
      planId: plan?.id ?? '',
      planTitle: plan?.title ?? '',
      planStartTimeId: pick.startTimeId,
      planStartTime: pick.startTime,
      planUnitItems: unitQuantitiesToPlanUnitItems(pick.unitQuantities),
      thumbnailUrl: image
    },
    displayInfo: {
      title: activity?.title ?? '',
      // As the SPA did: the base amount, whatever the display currency. Only
      // the editor's footer shows a total, and it works its own out.
      totalPrice: pick.unitQuantities.reduce(
        (sum, unit) => sum + unit.count * unit.amount,
        0
      ),
      totalQuantity: sumQuantities(pick.unitQuantities.map(unit => unit.count)),
      img: image
    }
  };
}

/**
 * Whether a pick still fits the main booking. A same-day-linked option must be
 * on the main product's date, and inside the time window around its start
 * time; with no main date — no plan open, or none picked — it fits nowhere.
 *
 * The SPA cleared such picks when the main date or time moved; here they are
 * only left out while they don't fit, and come back if the main booking moves
 * back.
 */
export function pickFitsMainBooking(
  pick: SelectedOption,
  option: OptionProduct | undefined,
  mainDate: string,
  mainStartTime: string
): boolean {
  if (!option || !isSameDayLinkage(option.timeLinkage)) return true;
  if (!mainDate || pick.targetDate !== mainDate) return false;
  if (!pick.startTime || !mainStartTime) return true;
  const { from, to } = computeOptionTimeWindow(
    option.timeLinkage,
    mainStartTime
  );
  return !isTimeOutsideWindow(pick.startTime, from, to);
}

/** The session's option forms: one per option with something picked. */
export const toOptionForms = (options: SelectedOption[]): Form[] =>
  options
    .filter(option => option.displayInfo.totalQuantity > 0)
    .map(option => ({
      productContentId: option.productContentId,
      activityForm: {
        activityId: option.activityForm.activityId,
        activityTitle: option.activityForm.activityTitle,
        displayCurrency: option.activityForm.displayCurrency,
        planId: option.activityForm.planId,
        planTitle: option.activityForm.planTitle,
        targetDate: option.targetDate,
        participationDate: option.isOpenDate ? undefined : option.targetDate,
        planStartTimeId: option.startTimeId,
        planStartTime: option.startTime,
        planUnitItems: unitQuantitiesToPlanUnitItems(option.unitQuantities),
        thumbnailUrl: option.displayInfo.img
      }
    }));
